import { CommonModule, NgFor, NgIf, NgStyle, SlicePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Etapa } from 'src/app/shared/model/gestion-proyectos/etapa/etapa';
import { Incidencias } from 'src/app/shared/model/incidencias/incidencia.model';
import { KanbanIncidencia } from 'src/app/shared/model/incidencias/kanbanincidencia.model';
import { Proyectos } from 'src/app/shared/model/incidencias/proyectos.model';
import { Constantes } from 'src/app/utilerias/constantes/constantes';
import { IncidenciasService } from '../incidencias.service';

@Component({
  selector: 'app-cliente-incidencias-dashboard',
  standalone: true,
  templateUrl: './cliente-incidencias-dashboard.component.html',
  styleUrls: ['./cliente-incidencias-dashboard.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    NgFor,
    NgIf,
    NgStyle,
    SlicePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
  ],
})
export class ClienteIncidenciasDashboardComponent implements OnInit {
  proyectos: Proyectos[] = [];
  etapas: Etapa[] = [];
  columnas: KanbanIncidencia[] = [];
  consulta = new ConsultaList<Incidencias>();

  selectProyecto: number = null;
  selectEtapa: number = null;
  regPag = 5;
  loading = false;

  listas: Record<string, Incidencias[]> = {};
  totales: Record<string, number> = {};

  constructor(
    private incidenciasService: IncidenciasService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.inicializaColumnas();
    this.consultaProyectos();
  }

  consultaProyectos(): void {
    this.incidenciasService.consultaProyectosClienteIncidencias().subscribe((res) => {
      this.proyectos = res?.list ?? [];
      if (this.proyectos.length === 1) {
        this.capturarProyecto(this.proyectos[0].id);
      }
    });
  }

  capturarProyecto(id: number): void {
    this.selectProyecto = id;
    this.selectEtapa = null;
    this.etapas = this.proyectos.find((proyecto) => proyecto.id === id)?.etapas ?? [];
    this.limpiaDatos();
  }

  capturarEtapa(id: number): void {
    this.selectEtapa = id;
    this.consultaIncidencias();
  }

  consultaIncidencias(): void {
    if (!this.selectProyecto || !this.selectEtapa) {
      return;
    }

    this.loading = true;
    this.consulta.bean = new Incidencias();
    this.consulta.bean.id_proyecto = this.selectProyecto;
    this.consulta.bean.id_etapa = this.selectEtapa;
    this.consulta.bean.estatus = null;
    this.consulta.regInicial = 0;
    this.consulta.regXPagina = this.regPag;

    this.incidenciasService.consultarIncidenciasClienteDashboard(this.consulta).subscribe({
      next: (res) => {
        const dto = res?.bean2?.incDTO;
        this.setLista(Constantes.ABIERTO, dto?.listaIncAbierto, dto?.totalItemsAbierto);
        this.setLista(Constantes.OBSERVACIONES, dto?.listaIncObservacion, dto?.totalItemsObservacion);
        this.setLista(Constantes.PROCESO, dto?.listaIncProceso, dto?.totalItemsProceso);
        this.setLista(Constantes.RESUELTO, dto?.listaIncResuelto, dto?.totalItemsResuelto);
        this.setLista(Constantes.REVISION, dto?.listaIncRevision, dto?.totalItemsRevision);
        this.setLista(Constantes.VOBO, dto?.listaIncVOBO, dto?.totalItemsVOBO);
        this.setLista(Constantes.CANCELADO, dto?.listaIncCancelado, dto?.totalItemsCancelado);
        this.loading = false;
      },
      error: () => {
        this.limpiaDatos();
        this.loading = false;
      },
    });
  }

  nuevaIncidencia(): void {
    const proyecto = this.proyectos.find((item) => item.id === this.selectProyecto);
    this.router.navigate(['/CLIENTE/incidencias/alta'], {
      state: { proyecto, idEtapa: this.selectEtapa, origenCliente: true },
    });
  }

  get totalIncidencias(): number {
    return Object.values(this.totales).reduce((total, value) => total + Number(value ?? 0), 0);
  }

  get ultimasIncidencias(): Incidencias[] {
    return Object.values(this.listas)
      .flat()
      .sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0))
      .slice(0, 6);
  }

  getLista(estatus: string): Incidencias[] {
    return this.listas[estatus] ?? [];
  }

  getTotal(estatus: string): number {
    return this.totales[estatus] ?? 0;
  }

  private setLista(estatus: string, lista: Incidencias[] = [], total = 0): void {
    this.listas[estatus] = (lista ?? []).map((incidencia) => {
      incidencia.descincidencia = incidencia.descincidencia ?? 'Sin descripción';
      return incidencia;
    });
    this.totales[estatus] = Number(total ?? 0);
  }

  private limpiaDatos(): void {
    this.listas = {};
    this.totales = {};
  }

  private inicializaColumnas(): void {
    this.columnas = [
      new KanbanIncidencia(Constantes.ABIERTO, [], 'error_outline', Constantes.ABIERTOCOLOR),
      new KanbanIncidencia(Constantes.OBSERVACIONES, [], 'rate_review', Constantes.OBSERVACIONESCOLOR),
      new KanbanIncidencia(Constantes.PROCESO, [], 'sync', Constantes.PROCESOCOLOR),
      new KanbanIncidencia(Constantes.RESUELTO, [], 'task_alt', Constantes.RESUELTOCOLOR),
      new KanbanIncidencia(Constantes.REVISION, [], 'preview', Constantes.REVISIONCOLOR),
      new KanbanIncidencia(Constantes.VOBO, [], 'verified', Constantes.VOBOCOLOR),
      new KanbanIncidencia(Constantes.CANCELADO, [], 'cancel', Constantes.CANCELADOCOLOR),
    ];
  }
}
